import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getPortalOrders, getPortalProfile } from '../../services/api';
import { Package, FileText, Truck, Clock, ArrowRight } from '@phosphor-icons/react';

const PortalDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [ordersRes, profileRes] = await Promise.all([
        getPortalOrders(),
        getPortalProfile()
      ]);
      setOrders(ordersRes.data);
      setProfile(profileRes.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const statusLabels = {
    new_lead: t('status.new_lead'),
    waiting_documents: t('status.waiting_documents'),
    documents_received: t('status.documents_received'),
    car_sourcing: t('status.car_sourcing'),
    vehicle_confirmed: t('status.vehicle_confirmed'),
    payment_pending: t('status.payment_pending'),
    payment_received: t('status.payment_received'),
    shipping_booked: t('status.shipping_booked'),
    in_transit: t('status.in_transit'),
    container_loaded: t('status.container_loaded'),
    bl_issued: t('status.bl_issued'),
    arrived_algeria: t('status.arrived_algeria'),
    customs_clearance: t('status.customs_clearance'),
    ready_delivery: t('status.ready_delivery'),
    delivered: t('status.delivered'),
    cancelled: t('status.cancelled')
  };

  const activeOrders = orders.filter(o => !['delivered', 'cancelled'].includes(o.status));
  const inTransitOrders = orders.filter(o => ['in_transit', 'container_loaded', 'bl_issued'].includes(o.status));
  const pendingDocs = orders.filter(o => o.status === 'waiting_documents');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="portal-dashboard">
      {/* Welcome */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">
          Welcome back, {user?.name?.split(' ')[0] || 'Client'}
        </h1>
        <p className="text-muted-foreground mt-1">Track your car imports and manage your account</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="card-brutalist">
          <div className="flex items-center justify-between">
            <div>
              <p className="overline mb-1">Active Orders</p>
              <p className="text-3xl font-bold">{activeOrders.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary flex items-center justify-center">
              <Package weight="duotone" className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card-brutalist">
          <div className="flex items-center justify-between">
            <div>
              <p className="overline mb-1">In Transit</p>
              <p className="text-3xl font-bold">{inTransitOrders.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-600 flex items-center justify-center">
              <Truck weight="duotone" className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card-brutalist">
          <div className="flex items-center justify-between">
            <div>
              <p className="overline mb-1">Pending Docs</p>
              <p className="text-3xl font-bold">{pendingDocs.length}</p>
            </div>
            <div className="w-12 h-12 bg-amber-500 flex items-center justify-center">
              <FileText weight="duotone" className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card-brutalist">
          <div className="flex items-center justify-between">
            <div>
              <p className="overline mb-1">Total Orders</p>
              <p className="text-3xl font-bold">{orders.length}</p>
            </div>
            <div className="w-12 h-12 bg-secondary flex items-center justify-center">
              <Clock weight="duotone" className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Profile Completion Alert */}
      {profile && (!profile.national_id && !profile.passport_number) && (
        <div className="card-brutalist bg-amber-50 border-amber-200 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-100 flex items-center justify-center">
              <FileText weight="duotone" className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-amber-800">Complete Your Profile</p>
              <p className="text-sm text-amber-700">Please add your ID/passport information to process orders faster.</p>
            </div>
            <Link to="/portal/profile">
              <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                Update Profile
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Recent Orders */}
      <div className="card-brutalist">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Your Orders</h2>
          <Link to="/portal/orders" className="text-sm text-primary hover:underline inline-flex items-center">
            View all <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Package weight="duotone" className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p>No orders yet</p>
            <p className="text-sm">Your car import orders will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.slice(0, 5).map((order, idx) => (
              <Link
                key={order.id}
                to={`/portal/orders/${order.id}`}
                className={`block p-4 border border-border hover:bg-secondary/30 transition-colors animate-fade-in stagger-${idx + 1}`}
                data-testid={`portal-order-${order.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono font-bold">{order.order_number}</span>
                  <span className={`status-badge status-${order.status}`}>
                    {statusLabels[order.status] || order.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  {order.vehicle && (
                    <span>{order.vehicle.brand_name} {order.vehicle.model_name}</span>
                  )}
                  {order.container_number && (
                    <span className="font-mono">Container: {order.container_number}</span>
                  )}
                  {order.estimated_arrival && (
                    <span>ETA: {order.estimated_arrival}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Quick Button Component
const Button = ({ variant, className, children, ...props }) => (
  <button
    className={`px-4 py-2 font-medium transition-colors ${
      variant === 'outline' 
        ? 'border bg-transparent hover:bg-secondary' 
        : 'bg-primary text-white hover:bg-primary/90'
    } ${className}`}
    {...props}
  >
    {children}
  </button>
);

export default PortalDashboard;
